import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { isEmpty, isEqual, size } from "lodash";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";
import { green, grey, red } from "@mui/material/colors";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import dateUtils from "@plannel/utils/date-util";
import codeUtil from "@plannel/pages/utils/code-util";
import targetInvSimulationService from "@plannel/services/ip/target-inv-simulation-service";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";

const { limitVersion, ipVersionStatusCode } = codeUtil;
const { RELEASED } = ipVersionStatusCode;
const REDUX_IP_VERSION_FILTER = "IP_VERSION_FILTER";
/* statusFilter is Array */
const IpVersionFilter = ({ viewName, ipVersionValue, onChangeFilter, statusFilters = []}) => {
  const { t } = useTranslation();

  const reduxDispatch = useDispatch();
  const ipVersionDataListReduxState = reduxUtil.getViewState(REDUX_IP_VERSION_FILTER);

  const [ipVersionDataList, setIpVersionDataList] = useState(ipVersionDataListReduxState?.ipVersion ? ipVersionDataListReduxState.ipVersion : []);
  const [ipVersionFilterDataList, setIpVersionFilterDataList] = useState([]);
  

  const onChangeHandler = (_, ipVersionValue) => {
    onChangeFilter(ipVersionValue);

    const createIpVersionReduxState = reduxUtil.createObjectState(viewName, { ipVersion: ipVersionValue });
    reduxDispatch(reduxUtil.updateViewState(createIpVersionReduxState));
  }
  
  const ipVersionDataStatusFilter = (results) => {
    let resultsFilter = [];

    if(!isEmpty(statusFilters)) {
      for (const result of results) {
        if (statusFilters.includes(result.statusCd)) {
          resultsFilter.push(result);
        }
      }
    } else {
      resultsFilter = results;
    }

    /* 기억 하고있는 redux data가 제거되거나 변경되는경우 dropdown list에 데이터가 존재하는지 체크하는 변수 */
    const existSelectedValueInResults = resultsFilter.find(result => isEqual(result.id, ipVersionValue?.id));

    /* Defualt IP Version Setting */
    if (!isEmpty(resultsFilter)) {
      if (isEmpty(existSelectedValueInResults)) {
        onChangeHandler(null, resultsFilter[0]);
      } else {
        onChangeHandler(null, existSelectedValueInResults);
      }
    }

    if (size(resultsFilter) > limitVersion) {
      const limitList = resultsFilter.slice(0, limitVersion)
      return limitList;
    }
    return resultsFilter;
  }

  const getScenarioData = () => {
    /* 이전에 불러온 Dropdown data list가 존재하는경우 다시 가져오지 않음. */
    if (isEmpty(ipVersionDataListReduxState?.ipVersion)) {
      let ipVersionResultData = [];

      targetInvSimulationService.getAll()
        .then(response => {
          const { results } = response.data;
          if(!isEmpty(results)) {
            ipVersionResultData = results;
          }

          const ipVersionFilterData = ipVersionDataStatusFilter(ipVersionResultData);

          setIpVersionDataList(ipVersionResultData);
          setIpVersionFilterDataList(ipVersionFilterData);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const createIpVersionDataListReduxState = reduxUtil.createObjectState(REDUX_IP_VERSION_FILTER, { ipVersion: ipVersionResultData });
          reduxDispatch(reduxUtil.updateViewState(createIpVersionDataListReduxState));
        })
    } else {
      setIpVersionFilterDataList(ipVersionDataStatusFilter(ipVersionDataList));
    }
  }

  const iconColor = planVersion => {
    if (planVersion) {
      if (isEqual(planVersion.statusCd, RELEASED)) {
        return grey[900];
      }
  
      return isEmpty(planVersion.execTs) ? red[600] : green[600];
    }
  
    return null;
  }

  const setToolTip = (version) => {
    if (version?.classification) {
      const { periodVal, periodEnd } = version.classification;
      const startDate = new ZDate(periodEnd);
      startDate.setMonth(startDate.getMonth() - periodVal + 1);
      startDate.setDate(1);
  
      const format = "yyyy-MM-dd";
  
      return `${dateUtils.formatDate(startDate, format)} ~ ${periodEnd}`
    }
  
    return null;
  }

  useEffect(() => {
    getScenarioData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <VersionAutocomplete
      options={ipVersionFilterDataList}
      value={ipVersionValue}
      onChange={onChangeHandler}
      label={t("version")}
      getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
      getIconColor={iconColor}
      getTooltip={setToolTip}
      showInputIcon={false}
      isOptionEqualToValue={(option, value) => option.name === value?.name}
    />
  )
}

const reloadIpVersionFilter = (reduxDispatch) => {
  const ipVersionReduxFilterState = reduxUtil.getViewState(REDUX_IP_VERSION_FILTER);

  reduxDispatch(reduxUtil.removeViewState(ipVersionReduxFilterState));
}

export default React.memo(IpVersionFilter);
export { reloadIpVersionFilter };